"use client";

import { useMemo, useRef, useState } from "react";
import Link from "next/link";

import { useFaqDrawer } from "@/lib/faq-context";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { resolveTopicName, type FaqTopic } from "@/lib/faq-topics";
import {
  faqInRegion,
  faqUrl,
  topicSlug,
  topicUrl,
  type FaqStory,
} from "@/lib/faqs";
import type { Region } from "@/lib/region";
import { normalizeQuery, tokenizeQuery } from "@/lib/search";

const RESULTS_PER_PAGE = 12;

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="9" cy="9" r="6" />
      <path d="m17 17-3.5-3.5" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function ChevronToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 transition-transform duration-200 ease-out ${
        open ? "rotate-90" : ""
      }`}
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export default function FaqIndexPage({
  faqs: initialFaqs,
  topics,
  region,
}: {
  faqs: FaqStory[];
  topics: FaqTopic[];
  region: Region;
}) {
  // Merge server props with FAQ context (context wins per-uuid so live
  // bridge edits flow through), then scope to the current region — the
  // context holds every region's FAQs.
  const { faqs: contextFaqs } = useFaqDrawer();
  const faqs = useMemo(() => {
    const byUuid = new Map(initialFaqs.map((f) => [f.uuid, f]));
    for (const f of contextFaqs) byUuid.set(f.uuid, f);
    return Array.from(byUuid.values()).filter((f) => faqInRegion(f, region));
  }, [contextFaqs, initialFaqs, region]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const faqsByTopic = useMemo(() => {
    const m = new Map<string, FaqStory[]>();
    for (const faq of faqs) {
      const slug = topicSlug(faq.content?.topic);
      if (!slug) continue;
      const list = m.get(slug);
      if (list) list.push(faq);
      else m.set(slug, [faq]);
    }
    return m;
  }, [faqs]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const [slug, list] of faqsByTopic) m.set(slug, list.length);
    return m;
  }, [faqsByTopic]);

  // Every topic that actually has FAQs, named from the datasource — so a
  // topic an editor adds shows up without a code change.
  const displayTopics = useMemo<FaqTopic[]>(
    () =>
      Array.from(faqsByTopic.keys())
        .map((value) => ({ value, name: resolveTopicName(value, topics) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [faqsByTopic, topics],
  );

  const normalised = normalizeQuery(query);
  const tokens = useMemo(() => tokenizeQuery(normalised), [normalised]);
  const matches = useMemo(() => {
    if (!normalised || tokens.length === 0) return [];
    return faqs.filter((faq) => {
      const name = faq.name.toLowerCase();
      return tokens.every((t) => name.includes(t));
    });
  }, [faqs, normalised, tokens]);

  const totalPages = Math.max(1, Math.ceil(matches.length / RESULTS_PER_PAGE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pagedMatches = matches.slice(
    (currentPage - 1) * RESULTS_PER_PAGE,
    currentPage * RESULTS_PER_PAGE,
  );

  const setQueryAndReset = (next: string) => {
    setQuery(next);
    setPage(1);
  };

  return (
    <article>
      <section className="bg-dusk-01">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2rem,4vw,3.5rem)] pb-[clamp(2.5rem,5vw,3.5rem)]">
          <div className="mx-auto max-w-[760px] text-center">
            <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-bold leading-[1.08] tracking-[-0.01em] text-purple-05">
              Frequently Asked Questions
            </h1>
            <p className="mx-auto mt-3 max-w-[480px] text-body text-black/65 lg:text-h6 lg:leading-normal">
              Quick answers to the most common questions about fertility
              treatments, testing, and what to expect.
            </p>
          </div>

          <div className="mx-auto mt-6 max-w-[640px] lg:mt-8">
            <div className="relative">
              <span className="pointer-events-none absolute top-1/2 left-5 -translate-y-1/2 text-purple-03">
                <SearchIcon />
              </span>
              <input
                type="search"
                name="faq-search"
                value={query}
                onChange={(e) => setQueryAndReset(e.target.value)}
                placeholder="Search for a question…"
                aria-label="Search FAQs"
                autoComplete="off"
                className="h-14 w-full rounded-full border border-purple-01/40 bg-white pl-12 pr-14 text-body text-black placeholder:text-black/55 outline-none transition-shadow focus:border-purple-03 focus:shadow-[0_4px_20px_rgba(106,95,218,0.18)] [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQueryAndReset("")}
                  aria-label="Clear search"
                  className="absolute top-1/2 right-3 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/5 text-black/60 transition-colors hover:bg-black/10 hover:text-black"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden
                  >
                    <path d="m3 3 8 8M11 3l-8 8" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,5vw,4.5rem)]">
        {normalised ? (
          <SearchResults
            matches={matches}
            pagedMatches={pagedMatches}
            query={normalised}
            region={region}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : (
          displayTopics.length > 0 && (
            <TopicsGrid
              topics={displayTopics}
              counts={counts}
              faqsByTopic={faqsByTopic}
              region={region}
            />
          )
        )}
      </div>
    </article>
  );
}

function SearchResults({
  matches,
  pagedMatches,
  query,
  region,
  currentPage,
  totalPages,
  onPageChange,
}: {
  matches: FaqStory[];
  pagedMatches: FaqStory[];
  query: string;
  region: Region;
  currentPage: number;
  totalPages: number;
  onPageChange: (n: number) => void;
}) {
  const { ref: gridRef, rovingProps } = useRovingFocus<HTMLUListElement>();
  const resultsTopRef = useRef<HTMLSpanElement>(null);

  const goToPage = (n: number) => {
    onPageChange(n);
    // Back to the top, with focus just before the results so the next Tab
    // lands on the first card.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      resultsTopRef.current?.focus({ preventScroll: true });
    });
  };

  if (matches.length === 0) {
    return (
      <div className="rounded-[16px] border border-black/10 bg-white p-8 text-center">
        <p className="text-h6 font-bold text-black">
          No questions match &ldquo;{query}&rdquo;
        </p>
        <p className="mt-2 text-body text-black/60">
          Try a different word or browse topics below.
        </p>
      </div>
    );
  }
  return (
    <section>
      <span ref={resultsTopRef} tabIndex={-1} className="sr-only" />
      <h2 className="text-h4 font-bold leading-[1.12] text-purple-05 lg:text-h2 lg:leading-[1.054]">
        {matches.length} {matches.length === 1 ? "result" : "results"} for &ldquo;{query}&rdquo;
      </h2>
      <ul
        ref={gridRef}
        className="mt-6 grid gap-3 md:grid-cols-2"
        {...rovingProps}
      >
        {pagedMatches.map((faq) => (
          <FaqRow key={faq.uuid} faq={faq} region={region} />
        ))}
      </ul>
      {totalPages > 1 && (
        <Pagination
          current={currentPage}
          total={totalPages}
          onChange={goToPage}
        />
      )}
    </section>
  );
}

function pageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...set]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

function Pagination({
  current,
  total,
  onChange,
}: {
  current: number;
  total: number;
  onChange: (n: number) => void;
}) {
  const window = pageWindow(current, total);
  const linkClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 cursor-pointer items-center justify-center rounded-full border border-black/10 px-3 text-button font-semibold text-purple-05 transition-colors hover:border-purple-03 hover:text-purple-03";
  const activeClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 items-center justify-center rounded-full bg-purple-03 px-3 text-button font-semibold text-white";
  const disabledClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 items-center justify-center rounded-full border border-black/10 px-3 text-button font-semibold text-black/30";

  // Entry tab stop is the next page so Tab into the pagination is ready to
  // advance. Roving items: [prev?, window number buttons, next?].
  const homeIndex =
    (current > 1 ? 1 : 0) +
    window.filter((w) => typeof w === "number" && w < current + 1).length;
  const { ref: navRef, rovingProps } = useRovingFocus<HTMLElement>({
    homeIndex,
  });

  return (
    <nav
      ref={navRef}
      aria-label="Search results pagination"
      className="mt-10 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
      {...rovingProps}
    >
      {current > 1 ? (
        <button
          type="button"
          data-roving
          onClick={() => onChange(current - 1)}
          aria-label="Previous page"
          className={linkClass}
        >
          ←
        </button>
      ) : (
        <span aria-hidden className={disabledClass}>
          ←
        </span>
      )}
      {window.map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} aria-hidden className="px-1 text-black/55">
            …
          </span>
        ) : (
          // The current page stays a focusable button so focus isn't dropped.
          <button
            key={p}
            type="button"
            data-roving
            onClick={() => onChange(p)}
            aria-current={p === current ? "page" : undefined}
            className={p === current ? activeClass : linkClass}
          >
            {p}
          </button>
        ),
      )}
      {current < total ? (
        <button
          type="button"
          data-roving
          onClick={() => onChange(current + 1)}
          aria-label="Next page"
          className={linkClass}
        >
          →
        </button>
      ) : (
        <span aria-hidden className={disabledClass}>
          →
        </span>
      )}
    </nav>
  );
}

function FaqRow({ faq, region }: { faq: FaqStory; region: Region }) {
  return (
    <li>
      <Link
        href={faqUrl(faq, region)}
        data-roving
        className="group flex h-full items-center justify-between gap-4 rounded-[12px] border border-black/10 bg-white px-5 py-4 transition-colors hover:border-purple-03 hover:bg-purple-05/5 lg:px-5 lg:py-5"
      >
        <span className="text-body font-semibold leading-tight text-black lg:text-[17px]">
          {faq.name}
        </span>
        <span className="shrink-0 text-purple-03">
          <ArrowIcon />
        </span>
      </Link>
    </li>
  );
}

function TopicsGrid({
  topics,
  counts,
  faqsByTopic,
  region,
}: {
  topics: FaqTopic[];
  counts: Map<string, number>;
  faqsByTopic: Map<string, FaqStory[]>;
  region: Region;
}) {
  const [openTopic, setOpenTopic] = useState<string | null>(null);
  // The cards are a roving group: only the active card is a tab stop, so arrows
  // move between cards and Tab leaves the grid. `activeQuestion` does the same
  // within the open card's questions.
  const [activeCard, setActiveCard] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState(0);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const questionRefs = useRef<(HTMLAnchorElement | null)[][]>([]);

  const toggleCard = (value: string) => {
    setOpenTopic((o) => (o === value ? null : value));
    setActiveQuestion(0);
  };

  // Move to another card via arrows: close any open card so the only tab stops
  // are the card headers.
  const goToCard = (next: number) => {
    setActiveCard(next);
    setOpenTopic(null);
    setActiveQuestion(0);
    headerRefs.current[next]?.focus();
  };

  const totalCount = useMemo(() => {
    let total = 0;
    for (const list of faqsByTopic.values()) total += list.length;
    return total;
  }, [faqsByTopic]);

  return (
    <section>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-h6 font-bold text-purple-05 lg:text-h5">
          Browse by topic
        </h2>
        <span className="text-button text-black/55">
          {totalCount} {totalCount === 1 ? "question" : "questions"}
        </span>
      </div>
      <ul className="mt-6 [@media(min-width:680px)]:columns-2 [@media(min-width:680px)]:gap-4 lg:mt-8 lg:columns-3">
        {topics.map((topic, index) => {
          const count = counts.get(topic.value) ?? 0;
          const isOpen = openTopic === topic.value;
          const topicFaqs = faqsByTopic.get(topic.value) ?? [];
          return (
            <li key={topic.value} className="mb-4 break-inside-avoid">
              <div
                className={`overflow-hidden rounded-[14px] border bg-white transition-colors has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-purple-03 ${
                  isOpen
                    ? "border-purple-03"
                    : "border-black/10 hover:border-purple-03"
                }`}
              >
                <div
                  ref={(el) => {
                    headerRefs.current[index] = el;
                  }}
                  role="button"
                  tabIndex={index === activeCard ? 0 : -1}
                  onFocus={() => setActiveCard(index)}
                  onClick={() => toggleCard(topic.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleCard(topic.value);
                    } else if (e.key === "Escape" && isOpen) {
                      e.preventDefault();
                      setOpenTopic(null);
                    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                      e.preventDefault();
                      goToCard(Math.min(index + 1, topics.length - 1));
                    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                      e.preventDefault();
                      goToCard(Math.max(index - 1, 0));
                    }
                  }}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-5 text-left focus-visible:outline-none lg:px-6 lg:py-6"
                >
                  <span>
                    <Link
                      href={topicUrl(topic.value, region)}
                      tabIndex={-1}
                      onClick={(e) => e.stopPropagation()}
                      className="text-body font-bold leading-tight text-black transition-colors hover:text-purple-04 lg:text-h6"
                    >
                      {topic.name}
                    </Link>
                    <span className="mt-1.5 block text-body text-black/55">
                      {count} {count === 1 ? "question" : "questions"}
                    </span>
                  </span>
                  <span className="shrink-0 text-purple-03">
                    <ChevronToggleIcon open={isOpen} />
                  </span>
                </div>
                <div
                  inert={!isOpen}
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="border-t border-black/10 bg-dusk-01/50 px-5 py-5 lg:px-6 lg:py-6">
                      <ul>
                        {topicFaqs.map((faq, qIndex) => (
                          <li key={faq.uuid} className="mb-1 last:mb-0">
                            <Link
                              href={faqUrl(faq, region)}
                              ref={(el) => {
                                (questionRefs.current[index] ??= [])[qIndex] =
                                  el;
                              }}
                              tabIndex={qIndex === activeQuestion ? 0 : -1}
                              onKeyDown={(e) => {
                                const list = questionRefs.current[index] ?? [];
                                if (e.key === "ArrowDown") {
                                  e.preventDefault();
                                  const next = Math.min(
                                    qIndex + 1,
                                    list.length - 1,
                                  );
                                  setActiveQuestion(next);
                                  list[next]?.focus();
                                } else if (e.key === "ArrowUp") {
                                  e.preventDefault();
                                  const prev = Math.max(qIndex - 1, 0);
                                  setActiveQuestion(prev);
                                  list[prev]?.focus();
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  setOpenTopic(null);
                                  headerRefs.current[index]?.focus();
                                }
                              }}
                              className="group flex items-start gap-3 rounded-md px-3 py-1.5 text-body leading-normal text-black/80 transition-colors hover:text-purple-04"
                            >
                              <span
                                aria-hidden
                                className="mt-2 size-1.5 shrink-0 rounded-full bg-purple-03/35 transition-colors group-hover:bg-purple-03"
                              />
                              <span className="flex-1">{faq.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
